/*!
 * env-fee.js  -  Miljoetillaeg pr. ordre (Shopify theme asset)
 *
 * Holder praecis 1 stk. af gebyrvarianten i kurven, naar der ligger mindst en
 * anden vare, og fjerner den igen, naar kurven ellers er tom.
 *
 * Princip:
 *  1. Gebyret laegges i kurven FOER temaets eget add-to-cart-kald slippes igennem.
 *     Temaets kurv/skuffe tegnes derfor korrekt foerste gang: ingen reload, intet blink,
 *     og gebyret ender nederst i kurven.
 *  2. Efter alle kurv-aendringer (add/change/update/clear) efterkontrolleres kurven.
 *     Fjerner kunden gebyret eller aendrer antal, rettes det med det samme.
 *  3. Klik paa "Til kassen" holdes tilbage, indtil kurven er kontrolleret.
 *  4. Kontrol ved sideindlaesning, retur via browserens back-cache og naar fanen faar fokus.
 *
 * Konfiguration kommer fra snippets/env-fee.liquid via window.EnvFee.
 *
 * Tema-hook: scriptet udsender 'envfee:cart-changed' paa document, naar det selv har
 * aendret kurven. Kald event.preventDefault() for selv at gentegne kurven i stedet for
 * scriptets standard (reload paa kurvesiden / Dawn pub-sub i skuffen).
 *
 * Klientside-kode kan ikke give 100 % garanti. Saet et Shopify Flow op som backstop.
 */
(() => {
  'use strict';

  if (window.__envFeeInit) return;
  window.__envFeeInit = true;

  const boot = window.EnvFee || {};
  const FEE_ID = Number(boot.variantId);
  if (!FEE_ID) {
    console.warn('[env-fee] variantId mangler - scriptet er ikke aktivt');
    return;
  }

  const DEBUG = !!boot.debug;
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  const CHECKOUT_SELECTOR =
    'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]';
  const PRE_ADD_MAX_WAIT_MS = 2500;   // laeg-i-kurv maa aldrig haenge paa gebyret
  const CHECKOUT_MAX_WAIT_MS = 3000;  // checkout maa aldrig haenge paa kontrollen
  const RELOAD_KEY = 'envFeeReloads';

  const nativeFetch = window.fetch.bind(window);
  const log = (...a) => { if (DEBUG) console.log('[env-fee]', ...a); };
  const warn = (...a) => console.warn('[env-fee]', ...a);
  const isFee = (item) => Number(item.variant_id) === FEE_ID;
  const withTimeout = (p, ms) => Promise.race([p, new Promise((r) => setTimeout(r, ms))]);

  // ---- Tilstand (null = ukendt) -------------------------------------------
  let hasFee = null;
  let lastOthers = null;
  if (typeof boot.feeQty === 'number' && typeof boot.otherQty === 'number') {
    hasFee = boot.feeQty > 0;
    lastOthers = boot.otherQty;
  }

  // ---- Alle egne kurv-kald koeres i raekkefoelge --------------------------
  let queue = Promise.resolve();
  function enqueue(task) {
    const run = queue.then(task);
    queue = run.catch(() => {});
    return run;
  }

  async function getCart() {
    const res = await nativeFetch(ROOT + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) throw new Error('cart.js ' + res.status);
    return res.json();
  }

  async function post(path, body) {
    const res = await nativeFetch(ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(path + ' ' + res.status + ' ' + text.slice(0, 200));
    }
    return res.json();
  }

  const addFee = () => post('cart/add.js', { items: [{ id: FEE_ID, quantity: 1 }] });

  // ---- Kernen: bring kurven i korrekt tilstand ----------------------------
  function sync(opts) {
    return enqueue(() => doSync(opts || {}));
  }

  async function doSync({ silent }) {
    const cart = await getCart();
    const feeLines = cart.items.filter(isFee);
    const others = cart.items.length - feeLines.length;
    const feeQty = feeLines.reduce((n, i) => n + i.quantity, 0);
    const before = lastOthers;
    let changed = false;

    if (others > 0 && feeQty === 0) {
      await addFee();
      changed = true;
      log('gebyr tilfoejet');
    } else if (others > 0 && (feeQty !== 1 || feeLines.length !== 1)) {
      const updates = {};
      feeLines.forEach((line, idx) => { updates[line.key] = idx === 0 ? 1 : 0; });
      await post('cart/update.js', { updates });
      changed = true;
      log('gebyr-antal rettet til 1');
    } else if (others === 0 && feeQty > 0) {
      const updates = {};
      feeLines.forEach((line) => { updates[line.key] = 0; });
      await post('cart/update.js', { updates });
      changed = true;
      log('gebyr fjernet - kurven er ellers tom');
    }

    hasFee = others > 0;
    lastOthers = others;

    if (changed && !silent) refreshUI({ emptied: others === 0 && before > 0 });
    return changed;
  }

  // Kaldes lige foer temaets eget add-kald slippes igennem.
  function ensureFeeBeforeAdd() {
    if (hasFee === true) return Promise.resolve();
    return enqueue(async () => {
      if (hasFee === true) return;
      if (hasFee === null) {
        const cart = await getCart();
        hasFee = cart.items.some(isFee);
        if (hasFee) return;
      }
      await addFee();
      hasFee = true;
      log('gebyr lagt i kurven foer produktet');
    }).catch((e) => warn('kunne ikke for-tilfoeje gebyr:', e.message));
  }

  let timer = null;
  function scheduleSync(delay) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      sync().catch((e) => warn('kontrol fejlede:', e.message));
    }, delay == null ? 150 : delay);
  }

  // ---- Gentegning af kurven, naar scriptet selv har aendret den -----------
  const onCartPage = () => /\/cart\/?$/.test(location.pathname);

  function safeReload() {
    // Vaern mod reload-loekke: hoejst 2 reloads pr. 15 sekunder.
    let ok = false;
    try {
      const now = Date.now();
      const recent = JSON.parse(sessionStorage.getItem(RELOAD_KEY) || '[]')
        .filter((t) => now - t < 15000);
      if (recent.length < 2) {
        recent.push(now);
        sessionStorage.setItem(RELOAD_KEY, JSON.stringify(recent));
        ok = true;
      } else {
        warn('reload sprunget over (loekke-vaern)');
      }
    } catch (_) { /* ingen sessionStorage: hellere ingen reload end en loekke */ }
    if (ok) location.reload();
  }

  function refreshUI(detail) {
    const ev = new CustomEvent('envfee:cart-changed', { detail, cancelable: true });
    if (!document.dispatchEvent(ev)) return; // temaet har selv overtaget gentegningen

    if (onCartPage() || detail.emptied) return safeReload();

    // Dawn-baserede temaer: faa kurv-skuffen til at hente sig selv igen.
    try {
      if (typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'env-fee' });
      }
    } catch (e) { log(e); }
  }

  // ---- Opfang temaets (og apps') kurv-kald: fetch -------------------------
  function mutationKind(url) {
    try {
      const u = new URL(url, location.href);
      if (u.origin !== location.origin) return null;
      const m = u.pathname.match(CART_MUTATION);
      return m ? m[1] : null;
    } catch (_) {
      return null;
    }
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || String(input);
    const kind = mutationKind(url);
    if (!kind) return nativeFetch(input, init);

    const ready = kind === 'add'
      ? withTimeout(ensureFeeBeforeAdd(), PRE_ADD_MAX_WAIT_MS)
      : Promise.resolve();

    return ready.then(() => nativeFetch(input, init)).then(
      (res) => { scheduleSync(); return res; },
      (err) => { scheduleSync(); throw err; }
    );
  };

  // ---- ... og XMLHttpRequest (aeldre temaer / jQuery) ---------------------
  const xhrProto = XMLHttpRequest.prototype;
  const xhrOpen = xhrProto.open;
  const xhrSend = xhrProto.send;

  xhrProto.open = function (method, url) {
    this.__envFeeKind = mutationKind(url);
    this.__envFeeAsync = arguments.length < 3 || arguments[2] !== false;
    return xhrOpen.apply(this, arguments);
  };

  xhrProto.send = function () {
    const kind = this.__envFeeKind;
    if (!kind) return xhrSend.apply(this, arguments);

    this.addEventListener('loadend', () => scheduleSync());

    if (kind === 'add' && hasFee !== true && this.__envFeeAsync) {
      const args = arguments;
      withTimeout(ensureFeeBeforeAdd(), PRE_ADD_MAX_WAIT_MS).then(() => xhrSend.apply(this, args));
      return;
    }
    return xhrSend.apply(this, arguments);
  };

  // ---- Sidste port: klik paa "Til kassen" ---------------------------------
  document.addEventListener('click', (e) => {
    const el = e.target && e.target.closest ? e.target.closest(CHECKOUT_SELECTOR) : null;
    if (!el || el.__envFeePass) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    clearTimeout(timer);

    withTimeout(sync({ silent: true }), CHECKOUT_MAX_WAIT_MS)
      .catch((err) => warn('kontrol foer checkout fejlede:', err && err.message))
      .then(() => {
        // Slip klikket igennem igen, saa temaets egne handlers (fx vilkaars-checkbox) stadig koerer.
        el.__envFeePass = true;
        el.click();
        setTimeout(() => { el.__envFeePass = false; }, 1500);
      });
  }, true);

  // ---- Opstart -------------------------------------------------------------
  function init() {
    const knownConsistent = hasFee !== null && (
      (lastOthers > 0 && boot.feeQty === 1) ||
      (lastOthers === 0 && boot.feeQty === 0)
    );
    if (!knownConsistent) scheduleSync(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', (e) => { if (e.persisted) scheduleSync(0); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleSync(0); });

  // Til fejlsoegning i konsollen: EnvFee.sync()
  boot.sync = sync;
  window.EnvFee = boot;
})();
